import {pushError} from '@js/actions/errorManagement';

export function patchWorkerErrorReporting() {
    if (typeof window === 'undefined' || typeof window.Worker !== 'function' || window.Worker.__errorReportingPatched) {
        return;
    }

    const NativeWorker = window.Worker;

    const describeUrl = (scriptUrl) => {
        try {
            return typeof scriptUrl === 'string' ? scriptUrl : String(scriptUrl);
        } catch {
            return 'unknown';
        }
    };

    const workerLabel = (url) => {
        if (url.startsWith('blob:')) {
            return 'blob-worker';
        }
        if (url.startsWith('data:')) {
            return 'data-worker';
        }
        const path = url.split(/[?#]/)[0];
        const base = path.substring(path.lastIndexOf('/') + 1);
        return base || url;
    };

    class ReportingWorker extends NativeWorker {
        constructor(scriptUrl, options) {
            super(scriptUrl, options);

            const workerUrl = describeUrl(scriptUrl);
            const label = workerLabel(workerUrl);

            this.addEventListener('error', (event) => {
                const rawMessage = event.message || (event.error && event.error.message) || '';
                const location = [event.filename, event.lineno, event.colno]
                    .filter((part) => part !== undefined && part !== null && part !== '')
                    .join(':');
                const sanitized = !event.error && (!rawMessage || rawMessage === 'Script error.');

                const descriptiveMessage = [
                    `Worker error in ${label}`,
                    rawMessage,
                    location && `at ${location}`,
                    sanitized && '(details hidden: cross-origin worker script)'
                ].filter(Boolean).join(' — ');

                // Cross-origin worker scripts (maplibre's blob worker served from
                // assets.map-planner.com) surface opaque "Script error." events with no error
                // object: the browser hides every detail for security. Nothing is actionable and
                // a worker failure that actually breaks the map is already caught by the map load
                // watchdog — so drop these instead of flooding Sentry with detail-less noise.
                if (sanitized) {
                    return;
                }

                let error;
                if (event.error instanceof Error) {
                    error = event.error;
                } else {
                    error = new Error(descriptiveMessage);
                    error.name = 'WorkerError';
                }

                pushError(error, {
                    source: 'web-worker',
                    workerUrl,
                    workerLabel: label,
                    message: rawMessage || undefined,
                    filename: event.filename || undefined,
                    lineno: event.lineno || undefined,
                    colno: event.colno || undefined,
                    sanitized,
                    fingerprint: sanitized
                        ? ['worker-error', 'cross-origin-hidden']
                        : ['worker-error', label, rawMessage || 'no-message']
                });
            });

            this.addEventListener('message', (event) => {
                const detail = event.data && event.data.__mpWorkerError;
                if (!detail) {
                    return;
                }

                const error = new Error(detail.message || 'Worker error (no message)');
                error.name = 'WorkerError';
                if (detail.stack) {
                    error.stack = detail.stack;
                }

                pushError(error, {
                    source: 'web-worker',
                    workerUrl,
                    workerLabel: label,
                    message: detail.message || undefined,
                    filename: detail.filename || undefined,
                    lineno: detail.lineno || undefined,
                    colno: detail.colno || undefined,
                    unhandledRejection: detail.unhandledRejection || undefined,
                    relayed: true,
                    fingerprint: ['worker-error', 'relayed', detail.message || 'no-message']
                });
            });

            // Structured-clone deserialization failure on an incoming message.
            this.addEventListener('messageerror', () => {
                pushError(new Error(`Worker messageerror in ${label} (message could not be deserialized)`), {
                    source: 'web-worker',
                    workerUrl,
                    workerLabel: label,
                    fingerprint: ['worker-messageerror', label]
                });
            });
        }
    }

    ReportingWorker.__errorReportingPatched = true;
    window.Worker = ReportingWorker;
}
